import { Injectable, inject } from '@angular/core';

import { AuthErrorMessage } from '../../../errors/loan/auth-error-messages.enum';
import { LoanErrorMessage } from '../../../errors/loan/loan-error-messages.enum';
import { ForgotPasswordRequest, ForgotPasswordResponse } from '../../../models/forgot-password.model';
import { ApiResponse } from '../../../models/api-response.model';
import { Bank } from '../../../models/bank.model';
import { CredentialsDto } from '../../../models/dtos/credentials.dto';
import { JwtTokenDto } from '../../../models/dtos/jwt-token.dto';
import { LoanDetailDto } from '../../../models/dtos/loan-detail.dto';
import { LoanDto } from '../../../models/dtos/loan.dto';
import { LoanStatementDto } from '../../../models/dtos/loan-statement.dto';
import { PaymentHistoryItemDto } from '../../../models/dtos/payment-history-item.dto';
import { PaymentDto } from '../../../models/dtos/payment.dto';
import { PendingPaymentDto } from '../../../models/dtos/pending-payment.dto';
import { RecurringPaymentDetailsDto } from '../../../models/dtos/recurring-payment-details.dto';
import { UpcomingPaymentDto } from '../../../models/dtos/upcoming-payment.dto';
import { PaymentForm, RecurringPaymentForm } from '../../../models/payment.model';
import { ResetPasswordRequest } from '../../../models/reset-password.model';
import { UserCredentials } from '../../../models/user-credentials.model';
import { Wallet } from '../../../models/wallet.model';
import { RecurringPaymentDetailsMapper } from '../../../mappers/recurring-payment-details.mapper';
import { MockDataStore } from './mock-data.store';
import { mockBanksByRoutingNumber } from '../banks/bank.mock';
import { mockJwtToken, validateMockCredentials } from '../auth/auth.mock';
import { mockLoanDtos, mockLoanDetailDtos } from '../loans/loan.dto.mock';
import { mockLoanStatementDtos } from '../loans/loan-statement.dto.mock';
import { mockPaymentHistoryDtos } from '../payments/payment-history.dto.mock';
import { mockPendingPayment } from '../payments/pending-payment.dto.mock';
import { mockUpcomingPayment } from '../payments/upcoming-payment.dto.mock';
import {
  getHistoricPaymentsUrl,
  getLoanDetailsUrl,
  getLoansUrl,
  getLoginUrl,
  getLogoutUrl,
  getPasswordForgotUrl,
  getPaymentsUrl,
  getPendingPaymentsUrl,
  getRefreshUrl,
  getRegisterUrl,
  getRecurringPaymentUrl,
  getStatementDownloadUrl,
  getStatementsUrl,
  getUpcomingPaymentsUrl,
  getValidateUrl,
  getWalletsUrl,
} from '../../utils/api-urls.utils';
import { matchUrlParamsByTemplate } from '../../utils/url-matcher.util';

/**
 * Mock PDF content for statement download (simulates a minimal PDF file)
 */
export const MOCK_PDF_CONTENT = '%PDF-1.4\n%mock pdf content for statement download\n%%EOF';

export const INVALID_REQUEST_BODY = 'Invalid request body';


// REST verbs as enum
export enum HttpMethod {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
}

/**
 * Union type for all possible mutation request body types
 */
export type MutationRequestBody = CredentialsDto
  | RecurringPaymentDetailsDto // For recurring payment updates
  | PaymentForm // No DTO implemented yet, using model directly
  | CredentialsDto // For login requests
  | Wallet // No DTO implemented yet, using model directly
  | ForgotPasswordRequest
  | ResetPasswordRequest
  | UserCredentials // For user registration requests
  | string // For JWT token in validate and refresh requests
  | null // For logout requests (no body)
  ;

/**
 * Types returned by getMockResponse
 */
type MockGetResponseData =
  | LoanDto[]
  | LoanDetailDto
  | LoanStatementDto[]
  | PaymentHistoryItemDto[]
  | UpcomingPaymentDto[]
  | PendingPaymentDto[]
  | Bank // No DTO implemented yet, using model directly
  | RecurringPaymentDetailsDto
  | Blob
  ;

/**
 * Types returned by handleMutationRequest
 */
type MockMutationResponseData =
  | JwtTokenDto
  | RecurringPaymentDetailsDto
  | PaymentDto
  | ForgotPasswordResponse
  | boolean; // For validate

// Type guard to check if value is HttpMethod
function isHttpMethod(value: string): value is HttpMethod {
  return (
    typeof value === 'string' &&
    Object.values(HttpMethod).includes(value as HttpMethod)
  );
}

// Type guard to check if value is CredentialsDto
function isCredentialsDto(value: unknown): value is CredentialsDto {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'username' in obj &&
    'password' in obj &&
    typeof obj["username"] === 'string' &&
    typeof obj["password"] === 'string'
  );
}

// Type guard to check if value is RecurringPaymentDetailsDto
function isRecurringPaymentDetailsDto(value: unknown): value is RecurringPaymentDetailsDto {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'status' in obj &&
    'billing_type' in obj &&
    'billing_start_date' in obj &&
    typeof obj["status"] === 'number' &&
    typeof obj["billing_type"] === 'number' &&
    typeof obj["billing_start_date"] === 'string'
  );
}

// Type guard to check if value is PaymentForm
function isPaymentForm(value: unknown): value is PaymentForm {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'isRecurring' in obj &&
    'loanId' in obj &&
    'amount' in obj &&
    'type' in obj &&
    typeof obj["isRecurring"] === 'boolean' &&
    (typeof obj["amount"] === 'number' || obj["amount"] === null) &&
    typeof obj["type"] === 'string'
  );
}

// Type guard to check if value is Wallet
function isWallet(value: unknown): value is Wallet {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'AccountType' in obj && typeof obj['AccountType'] === 'number' &&
    'Nickname' in obj && typeof obj['Nickname'] === 'string' &&
    
    // Currently sent as `undefined` by `PaymentService`.
    // 'AccountNumber' in obj && typeof obj['AccountNumber'] === 'string' &&
    // 'ABANumber' in obj && typeof obj['ABANumber'] === 'string' &&

    'Validated' in obj && typeof obj['Validated'] === 'number' &&
    'Active' in obj && typeof obj['Active'] === 'number'
  );
}

// Type guard to check if value is RecurringPaymentForm
function isRecurringPaymentForm(value: unknown): value is RecurringPaymentForm {
  if (!isPaymentForm(value)) return false;
  const obj = value as PaymentForm;
  return (
    obj.isRecurring === true &&
    typeof obj.loanId === 'string' &&
    obj.loanId.length > 0 &&
    typeof obj.period === 'string' &&
    obj.period.length > 0
  );
}

// Type guard to check if value is ForgotPasswordRequest
function isForgotPasswordRequest(value: unknown): value is ForgotPasswordRequest {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'email' in obj && typeof obj['email'] === 'string' &&
    'ssnLast4' in obj && typeof obj['ssnLast4'] === 'string'
  );
}

// Type guard to check if value is ResetPasswordRequest
function isResetPasswordRequest(value: unknown): value is ResetPasswordRequest {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'token' in obj && typeof obj['token'] === 'string' &&
    'newPassword' in obj && typeof obj['newPassword'] === 'string'
  );
}

// Type guard to check if value is UserCredentials
function isUserCredentials(value: unknown): value is UserCredentials {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    'username' in obj && typeof obj['username'] === 'string' &&
    'password' in obj && typeof obj['password'] === 'string' &&
    'confirmPassword' in obj && typeof obj['confirmPassword'] === 'string' &&
    'securityQuestion1' in obj && typeof obj['securityQuestion1'] === 'string' &&
    'answer1' in obj && typeof obj['answer1'] === 'string' &&
    'securityQuestion2' in obj && typeof obj['securityQuestion2'] === 'string' &&
    'answer2' in obj && typeof obj['answer2'] === 'string' &&
    'firstName' in obj && typeof obj['firstName'] === 'string' &&
    'lastName' in obj && typeof obj['lastName'] === 'string' &&
    'email' in obj && typeof obj['email'] === 'string' &&
    'dateOfBirth' in obj && typeof obj['dateOfBirth'] === 'string'
  );
}

// Type guard to check if value is MutationRequestBody
function isMutationRequestBody(value: unknown): value is MutationRequestBody {
  return (
    isCredentialsDto(value) ||
    isRecurringPaymentDetailsDto(value) ||
    isPaymentForm(value) ||
    isWallet(value) ||
    isForgotPasswordRequest(value) || // Added check
    isResetPasswordRequest(value) || // Added check
    isUserCredentials(value) || // Added check
    typeof value === 'string' || // For JWT token in validate/refresh requests
    value === null // For logout requests (no body)
  );
}

@Injectable({
  providedIn: 'root'
})
export class MockDataService {
  readonly #mockDataStore = inject(MockDataStore);

/**
   * Handles all HTTP requests by routing to appropriate method based on HTTP method
   */
  handleRequest(
    url: string,
    method: string,
    body?: unknown
  ): ApiResponse<MockGetResponseData | MockMutationResponseData> | null {
    // Validate HTTP method
    if (!isHttpMethod(method)) {
      return null;
    }

    // For mutation methods, validate body
    if ([HttpMethod.Post, HttpMethod.Put, HttpMethod.Delete].includes(method as HttpMethod)) {
      if (!isMutationRequestBody(body)) {
        return { success: false, error: INVALID_REQUEST_BODY };
      }
      return this.handleMutationRequest(url, method, body as MutationRequestBody);
    }

    // Handle read operations
    return this.getMockResponse(url, method);
  }

  getMockResponse(
    url: string,
    method: string,
  ): ApiResponse<MockGetResponseData> | null {
    // Loans endpoints
    if (url === getLoansUrl() && method === HttpMethod.Get) {
      // Flatten all loan arrays into a single array
      return { success: true, data: Object.values(mockLoanDtos).flat() };
    }

    // Check for loan details endpoint pattern
    const loanDetailsMatch = this.#matchLoanDetailsUrl(url);
    if (loanDetailsMatch && method === HttpMethod.Get) {
      const loanDetail = mockLoanDetailDtos[loanDetailsMatch];
      if (loanDetail) {
        return { success: true, data: loanDetail };
      }
      return { success: false, error: LoanErrorMessage.LOAN_NOT_FOUND };
    }

    // Check for payment history endpoint pattern
    const paymentHistoryMatch = this.#matchPaymentHistoryUrl(url);
    if (paymentHistoryMatch && method === HttpMethod.Get) {
      const history = mockPaymentHistoryDtos[paymentHistoryMatch];
      if (history) {
        return { success: true, data: history };
      }
      return { success: false, error: LoanErrorMessage.PAYMENT_HISTORY_NOT_FOUND };
    }

    // Check for loan statements endpoint pattern
    const statementsMatch = this.#matchStatementsUrl(url);
    if (statementsMatch && method === HttpMethod.Get) {
      const statements = mockLoanStatementDtos[statementsMatch];
      if (statements) {
        return { success: true, data: statements };
      }
      return { success: false, error: LoanErrorMessage.LOAN_STATEMENTS_NOT_FOUND };
    }

    // Check for statement download endpoint pattern
    const statementDownloadMatch = this.#matchStatementDownloadUrl(url);
    if (statementDownloadMatch && method === HttpMethod.Get) {
      const { loanId, rowId } = statementDownloadMatch;
      const statements = mockLoanStatementDtos[loanId];
      const statement = statements?.find((statement: LoanStatementDto) => statement.row_id === rowId);
      if (statement) {
        // Simulate a PDF blob response
        return {
          success: true,
          data: new Blob([
            MOCK_PDF_CONTENT
          ], { type: 'application/pdf' }),
        };
      }
      return { success: false, error: LoanErrorMessage.FILE_NOT_FOUND };
    }

    // Check for recurring payments GET endpoint pattern
    const recurringPaymentId = this.#matchRecurringPaymentId(url);
    if (recurringPaymentId && method === HttpMethod.Get) {
      const recurringPayment = this.#mockDataStore.getRecurringPayment(recurringPaymentId);
      if (recurringPayment) {
        return { success: true, data: recurringPayment };
      }
      return { success: false, error: LoanErrorMessage.RECURRING_PAYMENT_NOT_FOUND };
    }

    // Check for upcoming payments endpoint pattern
    const upcomingPaymentsMatch = this.#matchUpcomingPaymentsUrl(url);
    if (upcomingPaymentsMatch && method === HttpMethod.Get) {
      return { success: true, data: [mockUpcomingPayment] };
    }

    // Check for pending payments endpoint pattern
    const pendingPaymentsMatch = this.#matchPendingPaymentsUrl(url);
    if (pendingPaymentsMatch && method === HttpMethod.Get) {
      return { success: true, data: [mockPendingPayment] };
    }

    return null;
  }

  handleMutationRequest(
    url: string, 
    method: string, 
    body: MutationRequestBody
  ): ApiResponse<MockMutationResponseData> | null {

    // Authentication endpoints
    if (url === getLoginUrl() && method === HttpMethod.Post) {
      if (!isCredentialsDto(body)) {
        return { success: false, error: INVALID_REQUEST_BODY };
      }
      // Process login and return JWT token
      return this.#processLogin(body);
    }

    // Mock logout endpoint
    if (url === getLogoutUrl() && method === HttpMethod.Post) {
      return { success: true };
    }
    // Mock refresh endpoint
    if (url === getRefreshUrl() && method === HttpMethod.Post) {
      // Always return success for mock refresh
      return { success: true, data: {
        token: mockJwtToken
      }};
    }

    // Mock validate endpoint
    if (url === getValidateUrl() && method === HttpMethod.Post) {
      // Always return success for mock validate
      return { success: true, data: true };
    }

    // Mock password forgot endpoint
    if (url === getPasswordForgotUrl() && method === HttpMethod.Post) {
      // Always return success for mock password forgot
      return { success: true };
    }

    // Mock register endpoint
    if (url === getRegisterUrl() && method === HttpMethod.Post) {
      // Always return success for mock register
      return { success: true };
    }

    // Payment processing
    if (url === getPaymentsUrl() && method === HttpMethod.Post) {
      if (!isPaymentForm(body)) {
        return { success: false, error: INVALID_REQUEST_BODY };
      }
      // Simulate a successful payment
      const transactionId = this.#generateId('txn');
      if (isRecurringPaymentForm(body)) {
        this.#setupRecurringPayment(body);
      }
      return { success: true, data: { transactionId } };
    }

    // Wallet creation (mock)
    if (url === getWalletsUrl() && method === HttpMethod.Post) {
      // Accepts a Wallet as body, returns PaymentDto
      // No validation for now, just simulate success
      const transactionId = this.#generateId('wallet');
      return { success: true, data: { transactionId } };
    }

    // Check for PUT recurring payments
    const recurringPaymentIdPut = this.#matchRecurringPaymentId(url);
    if (recurringPaymentIdPut && method === HttpMethod.Put) {
      if (!isRecurringPaymentForm(body)) {
        return { success: false, error: INVALID_REQUEST_BODY };
      }
      const updated = this.#mockDataStore.updateRecurringPayment(recurringPaymentIdPut, body);
      if (updated) {
        const payment = this.#mockDataStore.getRecurringPayment(recurringPaymentIdPut);
        if (payment) {
          return { success: true, data: payment };
        }
      }
      return { success: false, error: LoanErrorMessage.RECURRING_PAYMENT_NOT_FOUND };
    }

    // Check for DELETE recurring payments
    const recurringPaymentIdDelete = this.#matchRecurringPaymentId(url);
    if (recurringPaymentIdDelete && method === HttpMethod.Delete) {
      const deleted = this.#mockDataStore.deleteRecurringPayment(recurringPaymentIdDelete);
      return deleted ? { success: true } : { success: false, error: LoanErrorMessage.RECURRING_PAYMENT_NOT_FOUND };
    }

    return null;
  }

  #matchLoanDetailsUrl(url: string): string | null {
    const params = matchUrlParamsByTemplate(getLoanDetailsUrl, ['loanId'], url);
    return params ? params['loanId'] : null;
  }

  #matchPaymentHistoryUrl(url: string): string | null {
    const params = matchUrlParamsByTemplate(getHistoricPaymentsUrl, ['loanId'], url);
    return params ? params['loanId'] : null;
  }

  #matchRecurringPaymentId(url: string): string | null {
    const params = matchUrlParamsByTemplate(getRecurringPaymentUrl, ['recurringPaymentId'], url);
    return params ? params['recurringPaymentId'] : null;
  }

  #matchStatementsUrl(url: string): string | null {
    const params = matchUrlParamsByTemplate(getStatementsUrl, ['loanId'], url);
    return params ? params['loanId'] : null;
  }

  #matchStatementDownloadUrl(url: string): { loanId: string, rowId: string } | null {
    const params = matchUrlParamsByTemplate(getStatementDownloadUrl, ['loanId', 'rowId'], url);
    if (!params) return null;
    return { loanId: params['loanId'], rowId: params['rowId'] };
  }

  #matchUpcomingPaymentsUrl(url: string): string | null {
    const params = matchUrlParamsByTemplate(getUpcomingPaymentsUrl, ['loanId'], url);
    return params ? params['loanId'] : null;
  }

  #matchPendingPaymentsUrl(url: string): string | null {
    const params = matchUrlParamsByTemplate(getPendingPaymentsUrl, ['loanId'], url);
    return params ? params['loanId'] : null;
  }

  #lookupBank(routingNumber: string): ApiResponse<Bank | undefined> {
    // Only validate format (9 digits), not checksum
    if (!/^\d{9}$/.test(routingNumber)) {
      return {
        success: false,
        error: LoanErrorMessage.INVALID_ROUTING_NUMBER_FORMAT,
      };
    }
    const bank = mockBanksByRoutingNumber[routingNumber];
    return bank
      ? { success: true, data: bank }
      : { success: false, error: LoanErrorMessage.BANK_NOT_FOUND };
  }

  #processLogin(loginData: CredentialsDto): ApiResponse<JwtTokenDto> {
    if (!loginData.username || !loginData.password) {
      return { success: false, error: AuthErrorMessage.USERNAME_PASSWORD_REQUIRED };
    }
    const authResponse = validateMockCredentials(loginData);
    if (authResponse) {
      return { success: true, data: authResponse };
    }
    return { success: false, error: AuthErrorMessage.INVALID_USERNAME_OR_PASSWORD };
  }

  #setupRecurringPayment(paymentData: RecurringPaymentForm): void {
    const newRecurringPayment: RecurringPaymentDetailsDto = {
      ...RecurringPaymentDetailsMapper.toDto(paymentData),
      row_id: this.#generateId('rp'),
    };

    const loan = this.#mockDataStore.getLoanDetail(paymentData.loanId);
    if (loan) {
      this.#mockDataStore.loanDetails[paymentData.loanId] = {
        ...loan,
        recurringPayment: newRecurringPayment,
        paymentSettings: {
          ...loan.paymentSettings,
          recurringPaymentExists: true,
        },
      };
    }
  }

  /**
   * Generates a unique ID with the given prefix
   */
  #generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  }
}
